// Counter's checkout (docs/plans/stations.md, board CounterCheckout): cash in a few taps and the
// change in big numbers. It ends in `completeSale`, the same as Quick bill, which stays the place
// for a discount, a customer or credit ("More options").
import { orderLines, type PaymentMethod } from '@ims/core'
import type { CustomerRow, OrderDetail } from '@ims/data'
import { Button, ConfirmDialog, Field, Modal, cx, useToast } from '@ims/ui'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { MoneyInput, useLookups } from '../components.tsx'
import { errorMessage, useEntitlements, useOnline, useSettings } from '../hooks.ts'
import {
  cartReducer,
  cartTotals,
  emptyCart,
  quickAmounts,
  type CartLine,
  type StockShortfall,
} from '../lib/cart.ts'
import { completeSale } from '../lib/complete-sale.ts'
import { money } from '../lib/format.ts'
import { billWithNumber } from '../lib/offline.ts'
import { KitchenCheckoutNote } from './kitchen-check.tsx'
import { orderCartLines, orderItemRows, orderServiceFor } from '../lib/orders.ts'
import { Receipt } from '../print.tsx'
import { useCan, useRepo, useStore, useWorkspace } from '../workspace.tsx'
import { printBill, useBillMeta } from './order-actions.tsx'
import { useServerName } from './server-dialog.tsx'

const METHODS: { value: PaymentMethod; label: string }[] = [
  { value: 'cash', label: 'Cash' },
  { value: 'wallet', label: 'Wallet' },
  { value: 'bank', label: 'Bank' },
  { value: 'card', label: 'Card' },
]

export function CounterCheckout({
  detail,
  where,
  onClose,
  onSplit,
}: {
  detail: OrderDetail
  where: string
  onClose: () => void
  onSplit?: () => void
}) {
  const repo = useRepo()
  const settings = useSettings()
  const lookups = useLookups()
  const entitlements = useEntitlements()
  const isManager = useCan('manager')
  const { current } = useWorkspace()
  const { engine } = useStore()
  const online = useOnline()
  const toast = useToast()
  const navigate = useNavigate()
  const billMeta = useBillMeta()
  const serverName = useServerName()
  const orderId = detail.order.id
  const bill = useMemo(() => orderLines(orderItemRows(detail.items)), [detail.items])
  const [lines, setLines] = useState<CartLine[] | null>(null)
  const [customer, setCustomer] = useState<CustomerRow | null>(null)
  const [method, setMethod] = useState<PaymentMethod>('cash')
  const [received, setReceived] = useState<number | null | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [short, setShort] = useState<{ list: StockShortfall[]; print: boolean } | null>(null)
  useEffect(() => {
    let stale = false
    void (async () => {
      const loaded = await orderCartLines(repo, bill)
      const buyer = detail.order.customer_id
        ? await repo.get('customers', detail.order.customer_id)
        : null
      if (stale) return
      setLines(cartReducer(emptyCart, { type: 'load', lines: loaded }).lines)
      setCustomer(buyer)
    })()
    return () => {
      stale = true
    }
  }, [repo, bill, detail.order.customer_id])

  const totals = cartTotals({ ...emptyCart, lines: lines ?? [] })
  const total = totals.error ? 0 : totals.totalPaisa
  const paid = received === undefined ? total : (received ?? 0)
  const change = Math.max(0, paid - total)
  const location = lookups.activeLocations[0]?.id ?? null
  const sendOut = detail.order.service_type === 'delivery'

  const finish = async (print: boolean, sellShort = false) => {
    if (!settings || !current || !lines || totals.error) return
    setBusy(true)
    try {
      const result = await completeSale({
        repo,
        settings,
        tenantId: current.tenant_id,
        monthLimit: entitlements?.limits.max_invoices_per_month ?? null,
        isManager,
        lines,
        billDiscountPaisa: totals.billDiscountPaisa,
        customer,
        location,
        paidPaisa: paid,
        creditPaisa: Math.max(0, total - paid),
        method,
        note: '',
        orderId,
        orderBill: bill,
        fromId: null,
        batchProductIds: [
          ...new Set(lines.filter((l) => l.trackBatches && l.productId).map((l) => l.productId!)),
        ],
        sellShort,
        onSettleError: (error) => toast.error(errorMessage(error)),
      })
      if (result.kind === 'refused') return toast.error(result.message)
      if (result.kind === 'short') return setShort({ list: result.short, print })
      toast.success(
        change > 0 ? `Sale complete. Give back ${money(change, settings)}` : 'Sale complete',
      )
      if (print) {
        const invoice = await billWithNumber(
          () => repo.get('sales_invoices', result.built.invoice.id),
          engine,
          online,
          result.built.invoice,
        )
        const [service, meta] = await Promise.all([
          orderServiceFor(repo, invoice, serverName),
          billMeta(repo, invoice),
        ])
        await printBill(
          repo,
          invoice.tenant_id,
          invoice,
          'receipt',
          (copyLabel) => (
            <Receipt
              invoice={invoice}
              lines={result.built.lines}
              settings={settings}
              balancePaisa={null}
              paidPaisa={paid}
              changePaisa={change}
              batches={{}}
              copyLabel={copyLabel}
              service={service}
              meta={meta}
            />
          ),
          (error) => toast.error(errorMessage(error)),
        )
      }
      navigate(
        detail.order.service_type === 'dine_in'
          ? '/station/counter'
          : `/station/counter?tab=${detail.order.service_type}`,
        { replace: true },
      )
      onClose()
    } catch (error) {
      toast.error(errorMessage(error))
    } finally {
      setBusy(false)
    }
  }
  const under = paid < total && !customer
  return (
    <>
      <Modal
        open
        onClose={onClose}
        size="sm"
        title={`${sendOut ? 'Send out' : 'Checkout'} · ${where}`}
        footer={
          <>
            <Button
              variant="secondary"
              size="lg"
              loading={busy}
              disabled={!lines || under}
              onClick={() => void finish(false)}
            >
              Finish
            </Button>
            <Button
              size="lg"
              loading={busy}
              disabled={!lines || under}
              onClick={() => void finish(true)}
            >
              Finish and print
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div className="flex items-baseline justify-between">
            <span className="text-muted">To pay</span>
            <output aria-label="To pay" className="tabular text-3xl font-extrabold">
              {money(total, settings)}
            </output>
          </div>
          <KitchenCheckoutNote detail={detail} where={where} />
          <div role="radiogroup" aria-label="Payment" className="grid grid-cols-4 gap-2">
            {METHODS.map((m) => (
              <button
                key={m.value}
                type="button"
                role="radio"
                aria-checked={method === m.value}
                onClick={() => setMethod(m.value)}
                className={cx(
                  'h-[3.25rem] rounded-xl border font-semibold',
                  method === m.value
                    ? 'border-primary bg-primary-light text-primary ring-1 ring-primary'
                    : 'border-border bg-surface hover:bg-soft',
                )}
              >
                {m.label}
              </button>
            ))}
          </div>
          <Field label="Received">
            {(id) => (
              <MoneyInput
                id={id}
                prefix="Rs."
                className="tabular text-xl font-bold"
                paisa={received === undefined ? total : received}
                onValue={(v) => setReceived(v)}
              />
            )}
          </Field>
          <div className="grid grid-cols-4 gap-2">
            {quickAmounts(total).map((a, i) => (
              <button
                key={a}
                type="button"
                onClick={() => setReceived(a)}
                className={cx(
                  'tabular h-[3.25rem] rounded-xl border font-bold',
                  paid === a
                    ? 'border-primary bg-primary-light ring-1 ring-primary'
                    : 'border-border bg-surface hover:bg-soft',
                )}
              >
                {i === 0 ? 'Exact' : (a / 100).toLocaleString('en-IN')}
              </button>
            ))}
          </div>
          {change > 0 && (
            <div className="flex items-baseline justify-between rounded-xl bg-success-light px-3.5 py-3 text-success">
              <span className="font-semibold">Give back</span>
              <output aria-label="Give back" className="tabular text-2xl font-extrabold">
                {money(change, settings)}
              </output>
            </div>
          )}
          {under && (
            <p className="text-sm font-medium text-warning">
              Less than the bill. Choose a customer in More options to sell on credit.
            </p>
          )}
          <div className="flex flex-wrap justify-between gap-x-4 gap-y-1">
            <Link
              to={`/bill?order=${orderId}`}
              className="text-sm font-semibold text-primary hover:underline"
            >
              More options: discount, customer or credit
            </Link>
            {onSplit && (
              <button
                type="button"
                className="text-sm font-semibold text-primary hover:underline"
                onClick={onSplit}
              >
                Split bill
              </button>
            )}
          </div>
        </div>
      </Modal>
      {short && (
        <ConfirmDialog
          open
          title="Sell more than you have?"
          message={short.list.map((x) => x.description).join(', ')}
          confirmLabel="Sell anyway"
          onClose={() => setShort(null)}
          onConfirm={() => {
            const { print } = short
            setShort(null)
            void finish(print, true)
          }}
        />
      )}
    </>
  )
}
